import React, { useState, useEffect } from 'react';
import {
  X,
  Zap,
  Sparkles,
  Truck,
  Package,
  Route,
  Clock,
  DollarSign,
  Globe,
  Leaf,
  RefreshCw,
  Plus,
  CheckSquare,
  Square,
  AlertTriangle
} from 'lucide-react';
import { formatDriverLabel, formatTruckLabel } from '../utils/dateUtils';

function minToTimeString(mins) {
  const clamped = Math.max(0, Math.min(1439, Math.round(mins)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function timeStringToMin(timeStr = '07:30') {
  if (!timeStr) return 450;
  const parts = String(timeStr).split(':').map(Number);
  const h = !isNaN(parts[0]) ? parts[0] : 7;
  const m = !isNaN(parts[1]) ? parts[1] : 30;
  return Math.max(0, Math.min(1439, h * 60 + m));
}

function IonicToggle({ checked, onChange, disabled = false }) {
  return (
    <label
      className="switch"
      style={{
        position: 'relative',
        display: 'inline-block',
        width: '40px',
        height: '20px',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        flexShrink: 0,
        margin: 0
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={e => !disabled && onChange(e.target.checked)}
        style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
      />
      <span
        style={{
          position: 'absolute',
          cursor: disabled ? 'not-allowed' : 'pointer',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: checked ? '#38bdf8' : '#475569',
          transition: '0.2s',
          borderRadius: '20px'
        }}
      >
        <span
          style={{
            position: 'absolute',
            height: '14px',
            width: '14px',
            left: checked ? '22px' : '3px',
            bottom: '3px',
            backgroundColor: '#ffffff',
            transition: '0.2s',
            borderRadius: '50%',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
          }}
        />
      </span>
    </label>
  );
}

export default function CrossOptimizationOptionsModal({
  isOpen,
  onClose,
  unassignedStops = [],
  jobs = [],
  trucks = [],
  drivers = [],
  driverShifts = [],
  rounds = [],
  stops = [],
  stopDependencies = [],
  currentDate = null,
  onLaunchCrossPreview
}) {
  const [selectedUnassignedStopIds, setSelectedUnassignedStopIds] = useState([]);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [selectedTruckIds, setSelectedTruckIds] = useState([]);
  const [truckTimeWindows, setTruckTimeWindows] = useState({});

  const [objective, setObjective] = useState('cost');
  const [allowCrossDays, setAllowCrossDays] = useState(false);
  const [allowCrossRounds, setAllowCrossRounds] = useState(false);
  const [allowHubRefills, setAllowHubRefills] = useState(false);
  const [hubReloadMins, setHubReloadMins] = useState(15);
  const [enforceTimeWindows, setEnforceTimeWindows] = useState(true);
  const [enforceWeight, setEnforceWeight] = useState(true);
  const [enforceVolume, setEnforceVolume] = useState(true);
  const [enforceDependencies, setEnforceDependencies] = useState(true);
  const [enforceMaxWorkTime, setEnforceMaxWorkTime] = useState(false);
  const [maxWorkHours, setMaxWorkHours] = useState(8);
  const [enforceForcedPauses, setEnforceForcedPauses] = useState(false);
  const [forcedPauses, setForcedPauses] = useState([
    { id: 1, duration_mins: 30, start_time: '12:00', end_time: '14:00' }
  ]);
  const [isLoading, setIsLoading] = useState(false);

  // Filter planned jobs
  const plannedJobs = (jobs || []).filter(j => j.status === 'planned');

  // Initialization when modal opens
  useEffect(() => {
    if (!isOpen) return;

    // By default, select all planned jobs for the day
    const jobIds = plannedJobs.map(j => j.id);
    setSelectedJobIds(jobIds);

    // Initial unassigned selection is empty (user can check or select all)
    setSelectedUnassignedStopIds([]);

    // Collect trucks from planned jobs
    const initialTruckIds = [];
    const initialTW = {};

    plannedJobs.forEach(j => {
      if (j.truck_id && !initialTruckIds.includes(j.truck_id)) {
        initialTruckIds.push(j.truck_id);
      }
    });

    // Populate truck time windows & drivers
    trucks.forEach(t => {
      const jobWithTruck = plannedJobs.find(j => j.truck_id === t.id);
      const driverId = jobWithTruck?.driver_id || t.default_driver_id || drivers[0]?.id || null;
      initialTW[t.id] = {
        driverId,
        startTime: t.planned_start_time || '07:30',
        endTime: t.planned_end_time || '17:00',
        shifts: [{ startTime: t.planned_start_time || '07:30', endTime: t.planned_end_time || '17:00' }]
      };
    });

    // If no trucks were on jobs, select all trucks
    setSelectedTruckIds(initialTruckIds.length > 0 ? initialTruckIds : trucks.map(t => t.id));
    setTruckTimeWindows(initialTW);
  }, [isOpen]);

  if (!isOpen) return null;

  // Counters
  const selectedJobStopsCount = (stops || [])
    .filter(s => selectedJobIds.includes(s.job_id))
    .length;
  const totalDeliveriesToOpti = selectedJobStopsCount + selectedUnassignedStopIds.length;

  const allTrucksHaveDrivers = selectedTruckIds.every(
    id => truckTimeWindows[id]?.driverId
  );

  // Toggle stop in bucket
  const handleToggleUnassignedStop = (stopId) => {
    setSelectedUnassignedStopIds(prev =>
      prev.includes(stopId) ? prev.filter(id => id !== stopId) : [...prev, stopId]
    );
  };

  // Toggle job
  const handleToggleJob = (jobId) => {
    const isChecked = selectedJobIds.includes(jobId);
    if (!isChecked) {
      setSelectedJobIds(prev => [...prev, jobId]);
      const targetJob = plannedJobs.find(j => j.id === jobId);
      if (targetJob?.truck_id && !selectedTruckIds.includes(targetJob.truck_id)) {
        setSelectedTruckIds(prev => [...prev, targetJob.truck_id]);
      }
    } else {
      setSelectedJobIds(prev => prev.filter(id => id !== jobId));
    }
  };

  // Toggle truck
  const handleToggleTruck = (truckId) => {
    setSelectedTruckIds(prev =>
      prev.includes(truckId) ? prev.filter(id => id !== truckId) : [...prev, truckId]
    );
  };

  // Shift & Driver handlers
  const handleUpdateTruckTW = (truckId, field, val) => {
    setTruckTimeWindows(prev => ({
      ...prev,
      [truckId]: {
        ...(prev[truckId] || { startTime: '07:30', endTime: '17:00', shifts: [{ startTime: '07:30', endTime: '17:00' }] }),
        [field]: val
      }
    }));
  };

  const handleUpdateShift = (truckId, shiftIdx, field, val) => {
    setTruckTimeWindows(prev => {
      const truckTW = prev[truckId] || { startTime: '07:30', endTime: '17:00', shifts: [{ startTime: '07:30', endTime: '17:00' }] };
      const currentShifts = [...(truckTW.shifts || [{ startTime: '07:30', endTime: '17:00' }])];
      currentShifts[shiftIdx] = { ...currentShifts[shiftIdx], [field]: val };
      return {
        ...prev,
        [truckId]: {
          ...truckTW,
          startTime: shiftIdx === 0 && field === 'startTime' ? val : truckTW.startTime,
          endTime: shiftIdx === currentShifts.length - 1 && field === 'endTime' ? val : truckTW.endTime,
          shifts: currentShifts
        }
      };
    });
  };

  const handleAddShift = (truckId) => {
    setTruckTimeWindows(prev => {
      const truckTW = prev[truckId] || { startTime: '07:30', endTime: '17:00', shifts: [{ startTime: '07:30', endTime: '17:00' }] };
      const currentShifts = [...(truckTW.shifts || [])];
      currentShifts.push({ startTime: '13:00', endTime: '17:00' });
      return {
        ...prev,
        [truckId]: { ...truckTW, shifts: currentShifts }
      };
    });
  };

  const handleRemoveShift = (truckId, shiftIdx) => {
    setTruckTimeWindows(prev => {
      const truckTW = prev[truckId] || { startTime: '07:30', endTime: '17:00', shifts: [{ startTime: '07:30', endTime: '17:00' }] };
      const currentShifts = (truckTW.shifts || []).filter((_, i) => i !== shiftIdx);
      return {
        ...prev,
        [truckId]: {
          ...truckTW,
          shifts: currentShifts.length > 0 ? currentShifts : [{ startTime: '07:30', endTime: '17:00' }]
        }
      };
    });
  };

  // Smart assign drivers
  const handleSmartAssignDrivers = () => {
    if (selectedTruckIds.length === 0) return;
    const targetDate = currentDate ? String(currentDate).slice(0, 10) : new Date().toISOString().slice(0, 10);
    const assignedDriverIds = new Set();
    const newTruckTW = { ...truckTimeWindows };

    selectedTruckIds.forEach(truckId => {
      const truck = trucks.find(t => t.id === truckId);
      if (!truck) return;

      const reqLic = truck.required_license || 'B';
      const isHeavy = reqLic === 'C' || reqLic === 'CE' || reqLic === 'D';

      const eligibleDrivers = drivers.filter(d => {
        if (assignedDriverIds.has(d.id)) return false;
        const dLic = d.driving_license || 'B';
        if (isHeavy) return dLic === 'C' || dLic === 'CE' || dLic === 'D';
        return true;
      });

      let chosenDriver = eligibleDrivers.find(d => {
        return (driverShifts || []).some(s => {
          if (s.driver_id !== d.id) return false;
          const sDate = String(s.start_datetime || '').slice(0, 10);
          return sDate === targetDate;
        });
      }) || eligibleDrivers[0];

      if (chosenDriver) {
        assignedDriverIds.add(chosenDriver.id);
        newTruckTW[truckId] = {
          ...(newTruckTW[truckId] || {}),
          driverId: chosenDriver.id
        };
      }
    });

    setTruckTimeWindows(newTruckTW);
  };

  // Launch Cross Optimization
  const handleLaunch = async () => {
    if (isLoading || totalDeliveriesToOpti === 0 || selectedTruckIds.length === 0 || !allTrucksHaveDrivers) {
      return;
    }

    // Validate pickup & delivery dependencies if enforced
    if (enforceDependencies) {
      const selectedJobStops = (stops || []).filter(s => selectedJobIds.includes(s.job_id));
      const selectedUnassigned = (unassignedStops || []).filter(us => selectedUnassignedStopIds.includes(us.id));
      const allSelectedStops = [...selectedJobStops, ...selectedUnassigned];
      const selectedStopIdsSet = new Set(allSelectedStops.map(s => s.id));

      for (const s of allSelectedStops) {
        let missingPartnerId = null;
        if (s.related_pickup_stop_id && !selectedStopIdsSet.has(parseInt(s.related_pickup_stop_id))) {
          missingPartnerId = s.related_pickup_stop_id;
        } else if (s.related_delivery_stop_id && !selectedStopIdsSet.has(parseInt(s.related_delivery_stop_id))) {
          missingPartnerId = s.related_delivery_stop_id;
        } else if (Array.isArray(stopDependencies)) {
          const depEdge = stopDependencies.find(d => (d.predecessor_stop_id === s.id && !selectedStopIdsSet.has(d.successor_stop_id)) || (d.successor_stop_id === s.id && !selectedStopIdsSet.has(d.predecessor_stop_id)));
          if (depEdge) {
            missingPartnerId = depEdge.predecessor_stop_id === s.id ? depEdge.successor_stop_id : depEdge.predecessor_stop_id;
          }
        }

        if (missingPartnerId) {
          const allStopsCombined = [...(stops || []), ...(unassignedStops || [])];
          const partnerStop = allStopsCombined.find(ps => ps.id === parseInt(missingPartnerId));
          const partnerName = partnerStop ? (partnerStop.stop_number || `STOP-${partnerStop.id}`) : `STOP-${missingPartnerId}`;
          alert(`Impossible de lancer l'optimisation croisée : le stop ${s.stop_number || `STOP-${s.id}`} est lié à ${partnerName} qui n'est pas sélectionné. Veuillez inclure les stops/missions liés ou décocher le respect des dépendances.`);
          return;
        }
      }
    }

    const truckConfigs = selectedTruckIds.map(id => {
      const tw = truckTimeWindows[id] || {};
      const shifts = tw.shifts && tw.shifts.length > 0
        ? tw.shifts
        : [{ startTime: tw.startTime || '07:30', endTime: tw.endTime || '17:00' }];
      return {
        id,
        startTime: shifts[0].startTime,
        endTime: shifts[shifts.length - 1].endTime,
        driverId: tw.driverId || null,
        shifts,
        maxWorkTimeMins: enforceMaxWorkTime ? Math.round((parseFloat(maxWorkHours) || 8) * 60) : null
      };
    });

    const options = {
      objective,
      flexibleStartTime: true,
      allowCrossDays,
      allowCrossRounds,
      allowHubRefills,
      hubReloadMins: Math.max(0, Math.min(240, parseInt(hubReloadMins, 10) || 0)),
      enforceTimeWindows,
      enforceWeight,
      enforceVolume,
      enforceDependencies,
      dependencyMode: enforceDependencies ? 'strict' : 'ignore',
      maxWorkTimeMins: enforceMaxWorkTime ? Math.round((parseFloat(maxWorkHours) || 8) * 60) : null,
      enforceSkills: false,
      enforceForcedPauses,
      forcedPauses: enforceForcedPauses ? forcedPauses : [],
      currentDate,
      truckConfigs
    };

    setIsLoading(true);
    try {
      if (onLaunchCrossPreview) {
        await onLaunchCrossPreview({
          selectedJobIds,
          selectedUnassignedStopIds,
          selectedTruckIds,
          options
        });
      }
      onClose();
    } catch (err) {
      console.error('Error running cross optimization preview:', err);
      alert("Erreur lors du calcul de l'optimisation croisée : " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1150 }}>
      <style>{`
        .no-spinner::-webkit-inner-spin-button,
        .no-spinner::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .no-spinner {
          -moz-appearance: textfield;
        }
        .cross-opti-col {
          background: #182030;
          border: 1px solid #334155;
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 0;
          overflow: hidden;
        }
        .cross-opti-col-header {
          background: #1e293b;
          border-bottom: 1px solid #334155;
          padding: 10px 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }
        .cross-opti-col-body {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          padding: 8px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
      `}</style>

      <div
        className="overlay-card"
        style={{
          width: '95vw',
          maxWidth: '1360px',
          height: '90vh',
          maxHeight: '860px',
          padding: '0',
          overflow: 'hidden',
          background: '#0f172a',
          border: '1px solid #334155',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* HEADER */}
        <div
          style={{
            background: '#1e293b',
            padding: '14px 20px',
            borderBottom: '1px solid #334155',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Zap size={20} color="#38bdf8" />
              <span>Optimisation Croisée Multi-Missions</span>
            </h3>

            <span
              style={{
                fontSize: '0.74rem',
                color: '#38bdf8',
                background: 'rgba(56, 189, 248, 0.12)',
                border: '1px solid rgba(56, 189, 248, 0.3)',
                padding: '2px 10px',
                borderRadius: '12px',
                fontWeight: '700'
              }}
            >
              {totalDeliveriesToOpti} livraison(s) sélectionnée(s) sur {selectedTruckIds.length} véhicule(s)
            </span>
          </div>

          <button
            onClick={onClose}
            disabled={isLoading}
            style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 4-COLUMN HORIZONTAL BODY */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            padding: '14px 18px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1.05fr 1.25fr',
            gap: '14px'
          }}
        >
          {/* COLUMN 1: BUCKET DELIVERIES */}
          <div className="cross-opti-col">
            <div className="cross-opti-col-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', fontSize: '0.82rem', color: '#f59e0b' }}>
                <Package size={15} color="#f59e0b" />
                <span>Livraisons à planifier ({selectedUnassignedStopIds.length}/{unassignedStops.length})</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  type="button"
                  onClick={() => setSelectedUnassignedStopIds(unassignedStops.map(s => s.id))}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                  title="Tout sélectionner"
                >
                  <CheckSquare size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedUnassignedStopIds([])}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                  title="Tout désélectionner"
                >
                  <Square size={13} />
                </button>
              </div>
            </div>

            <div className="cross-opti-col-body">
              {unassignedStops.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: '0.76rem', fontStyle: 'italic', padding: '16px', textAlign: 'center' }}>
                  Aucune livraison en attente dans le bucket
                </div>
              ) : (
                unassignedStops.map(us => {
                  const isChecked = selectedUnassignedStopIds.includes(us.id);
                  const twStr = us.time_window_start ? `${us.time_window_start.slice(0, 5)} - ${(us.time_window_end || '18:00').slice(0, 5)}` : '08:00 - 18:00';
                  return (
                    <label
                      key={us.id}
                      style={{
                        background: isChecked ? 'rgba(245, 158, 11, 0.12)' : '#1e293b',
                        border: isChecked ? '1px solid #f59e0b' : '1px solid #334155',
                        borderRadius: '6px',
                        padding: '6px 8px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleUnassignedStop(us.id)}
                        style={{ marginTop: '3px', accentColor: '#f59e0b' }}
                      />
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                          <span style={{ fontSize: '0.72rem', color: '#f59e0b', fontWeight: '700' }}>
                            {us.stop_number || `STOP-${us.id}`}
                          </span>
                          <span style={{ fontSize: '0.66rem', color: '#94a3b8', background: '#0f172a', padding: '1px 5px', borderRadius: '4px' }}>
                            {twStr}
                          </span>
                        </div>
                        <strong style={{ fontSize: '0.76rem', color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {us.client_name}
                        </strong>
                        <div style={{ fontSize: '0.68rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {us.city ? `${us.city} (${us.postal_code || ''})` : us.address}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {/* COLUMN 2: PLANNED MISSIONS */}
          <div className="cross-opti-col">
            <div className="cross-opti-col-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', fontSize: '0.82rem', color: '#38bdf8' }}>
                <Route size={15} color="#38bdf8" />
                <span>Missions à inclure ({selectedJobIds.length}/{plannedJobs.length})</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  type="button"
                  onClick={() => {
                    const allIds = plannedJobs.map(j => j.id);
                    setSelectedJobIds(allIds);
                    const associatedTruckIds = Array.from(new Set(plannedJobs.map(j => j.truck_id).filter(Boolean)));
                    setSelectedTruckIds(prev => Array.from(new Set([...prev, ...associatedTruckIds])));
                  }}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                  title="Tout sélectionner"
                >
                  <CheckSquare size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedJobIds([])}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                  title="Tout désélectionner"
                >
                  <Square size={13} />
                </button>
              </div>
            </div>

            <div className="cross-opti-col-body">
              {plannedJobs.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: '0.76rem', fontStyle: 'italic', padding: '16px', textAlign: 'center' }}>
                  Aucune mission planifiée pour ce jour
                </div>
              ) : (
                plannedJobs.map(j => {
                  const isChecked = selectedJobIds.includes(j.id);
                  const jobStopsCount = (stops || []).filter(s => s.job_id === j.id).length;
                  const round = (rounds || []).find(r => r.id === j.round_id);
                  const truck = (trucks || []).find(t => t.id === j.truck_id);
                  const timeRange = `${(j.estimated_departure || '07:30').slice(0, 5)} - ${j.real_end ? j.real_end.slice(0, 5) : '17:00'}`;

                  return (
                    <label
                      key={j.id}
                      style={{
                        background: isChecked ? 'rgba(56, 189, 248, 0.12)' : '#1e293b',
                        border: isChecked ? '1px solid #38bdf8' : '1px solid #334155',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleJob(j.id)}
                        style={{ marginTop: '3px', accentColor: '#38bdf8' }}
                      />
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                          <strong style={{ fontSize: '0.78rem', color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {j.display_name || `Mission #${j.id}`}
                          </strong>
                          <span style={{ fontSize: '0.68rem', color: '#38bdf8', fontWeight: '700', background: '#0f172a', padding: '1px 6px', borderRadius: '4px' }}>
                            {jobStopsCount} stops
                          </span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.68rem', color: '#94a3b8' }}>
                          {round && (
                            <span style={{ color: round.color || '#38bdf8', fontWeight: '600' }}>
                              {round.name}
                            </span>
                          )}
                          <span>•</span>
                          <span>{truck ? truck.name : 'Véhicule non assigné'}</span>
                        </div>

                        <div style={{ fontSize: '0.66rem', color: '#64748b' }}>
                          Plage : {timeRange}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {/* COLUMN 3: VEHICLES & SHIFTS */}
          <div className="cross-opti-col">
            <div className="cross-opti-col-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', fontSize: '0.82rem', color: '#ef4444' }}>
                <Truck size={15} color="#ef4444" />
                <span>Véhicules & Chauffeurs ({selectedTruckIds.length}/{trucks.length})</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  type="button"
                  onClick={handleSmartAssignDrivers}
                  style={{
                    background: 'rgba(56, 189, 248, 0.15)',
                    border: '1px solid #38bdf8',
                    color: '#38bdf8',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '0.68rem',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontWeight: '700'
                  }}
                  title="Affectation intelligente selon permis et disponibilités"
                >
                  <Sparkles size={11} color="#38bdf8" />
                  <span>Auto</span>
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedTruckIds(trucks.map(t => t.id))}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                  title="Tout sélectionner"
                >
                  <CheckSquare size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedTruckIds([])}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                  title="Tout désélectionner"
                >
                  <Square size={13} />
                </button>
              </div>
            </div>

            <div className="cross-opti-col-body">
              {trucks.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: '0.76rem', fontStyle: 'italic', padding: '16px', textAlign: 'center' }}>
                  Aucun véhicule disponible
                </div>
              ) : (
                trucks.map(t => {
                  const isChecked = selectedTruckIds.includes(t.id);
                  const wCap = t.max_payload_weight_kg ? (parseFloat(t.max_payload_weight_kg) >= 1000 ? `${(t.max_payload_weight_kg/1000).toFixed(1)}t` : `${Math.round(t.max_payload_weight_kg)}kg`) : '1.2t';
                  const vCap = t.max_payload_volume_m3 ? `${t.max_payload_volume_m3}m³` : '14m³';
                  const tw = truckTimeWindows[t.id] || { startTime: '07:30', endTime: '17:00', shifts: [{ startTime: '07:30', endTime: '17:00' }] };
                  const shifts = tw.shifts && tw.shifts.length > 0 ? tw.shifts : [{ startTime: tw.startTime || '07:30', endTime: tw.endTime || '17:00' }];

                  return (
                    <div
                      key={t.id}
                      style={{
                        background: isChecked ? 'rgba(239, 68, 68, 0.08)' : '#1e293b',
                        border: isChecked ? '1px solid #ef4444' : '1px solid #334155',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0 }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleTruck(t.id)}
                          style={{ accentColor: '#ef4444' }}
                        />
                        <span style={{ color: '#ffffff', flex: 1, fontWeight: '700', fontSize: '0.78rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {formatTruckLabel(t) || `Camion #${t.id}`}
                        </span>
                        <span style={{ color: '#ef4444', fontSize: '0.68rem', fontWeight: '700', whiteSpace: 'nowrap' }}>
                          {wCap} | {vCap}
                        </span>
                      </label>

                      {isChecked && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', paddingLeft: '22px', borderTop: '1px dashed #334155', paddingTop: '6px' }}>
                          {/* Chauffeur */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', color: '#94a3b8' }}>
                            <span style={{ width: '55px', flexShrink: 0 }}>Chauffeur:</span>
                            <select
                              value={tw.driverId ?? ''}
                              onChange={e => handleUpdateTruckTW(t.id, 'driverId', e.target.value ? parseInt(e.target.value) : '')}
                              style={{ background: '#0f172a', color: '#fff', border: '1px solid #334155', borderRadius: '4px', padding: '2px 4px', fontSize: '0.7rem', flex: 1 }}
                            >
                              <option value="">-- Sélectionner --</option>
                              {drivers.map(d => (
                                <option key={d.id} value={d.id}>
                                  {formatDriverLabel(d)} (Permis {d.driving_license || 'B'})
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Shifts */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.7rem', color: '#94a3b8' }}>
                            <span>Quarts ({shifts.length}):</span>
                            <button
                              type="button"
                              onClick={() => handleAddShift(t.id)}
                              style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center', gap: '2px', fontSize: '0.68rem', fontWeight: '600' }}
                              title="Ajouter un quart de travail"
                            >
                              <Plus size={11} /> Ajouter
                            </button>
                          </div>

                          {shifts.map((shift, shiftIdx) => (
                            <div key={shiftIdx} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', color: '#94a3b8' }}>
                              <input
                                type="time"
                                value={shift.startTime}
                                onChange={e => handleUpdateShift(t.id, shiftIdx, 'startTime', e.target.value)}
                                style={{ background: '#0f172a', color: '#fff', border: '1px solid #334155', borderRadius: '4px', padding: '1px 3px', fontSize: '0.7rem' }}
                              />
                              <span>à</span>
                              <input
                                type="time"
                                value={shift.endTime}
                                onChange={e => handleUpdateShift(t.id, shiftIdx, 'endTime', e.target.value)}
                                style={{ background: '#0f172a', color: '#fff', border: '1px solid #334155', borderRadius: '4px', padding: '1px 3px', fontSize: '0.7rem' }}
                              />
                              {shiftIdx > 0 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveShift(t.id, shiftIdx)}
                                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' }}
                                  title="Supprimer ce quart"
                                >
                                  <X size={12} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* COLUMN 4: OPTIMIZATION OPTIONS (MATCHING SINGLE JOB LOOK) */}
          <div className="cross-opti-col">
            <div className="cross-opti-col-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', fontSize: '0.82rem', color: '#22c55e' }}>
                <Sparkles size={15} color="#22c55e" />
                <span>Paramètres d'optimisation</span>
              </div>
            </div>

            <div className="cross-opti-col-body" style={{ padding: '10px 12px', gap: '10px' }}>
              {/* OBJECTIVES GRID */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                <div
                  onClick={() => setObjective('cost')}
                  style={{
                    background: objective === 'cost' ? 'rgba(56, 189, 248, 0.15)' : '#1e293b',
                    border: objective === 'cost' ? '2px solid #38bdf8' : '1px solid #334155',
                    padding: '8px 4px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <DollarSign size={16} color={objective === 'cost' ? '#38bdf8' : '#94a3b8'} style={{ margin: '0 auto 2px auto' }} />
                  <div style={{ fontSize: '0.74rem', fontWeight: '700', color: objective === 'cost' ? '#38bdf8' : '#f8fafc' }}>Coût</div>
                </div>

                <div
                  onClick={() => setObjective('distance')}
                  style={{
                    background: objective === 'distance' ? 'rgba(34, 197, 94, 0.15)' : '#1e293b',
                    border: objective === 'distance' ? '2px solid #22c55e' : '1px solid #334155',
                    padding: '8px 4px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <Globe size={16} color={objective === 'distance' ? '#22c55e' : '#94a3b8'} style={{ margin: '0 auto 2px auto' }} />
                  <div style={{ fontSize: '0.74rem', fontWeight: '700', color: objective === 'distance' ? '#22c55e' : '#f8fafc' }}>Distance</div>
                </div>

                <div
                  onClick={() => setObjective('duration')}
                  style={{
                    background: objective === 'duration' ? 'rgba(245, 158, 11, 0.15)' : '#1e293b',
                    border: objective === 'duration' ? '2px solid #f59e0b' : '1px solid #334155',
                    padding: '8px 4px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <Clock size={16} color={objective === 'duration' ? '#f59e0b' : '#94a3b8'} style={{ margin: '0 auto 2px auto' }} />
                  <div style={{ fontSize: '0.74rem', fontWeight: '700', color: objective === 'duration' ? '#f59e0b' : '#f8fafc' }}>Durée</div>
                </div>

                <div
                  style={{
                    background: '#1e293b',
                    border: '1px solid #334155',
                    padding: '8px 4px',
                    borderRadius: '6px',
                    cursor: 'not-allowed',
                    textAlign: 'center',
                    opacity: 0.45
                  }}
                  title="Optimisation de l'empreinte carbone (bientôt disponible)"
                >
                  <Leaf size={16} color="#94a3b8" style={{ margin: '0 auto 2px auto' }} />
                  <div style={{ fontSize: '0.74rem', fontWeight: '700', color: '#94a3b8' }}>CO₂</div>
                </div>
              </div>

              {/* TEMPS DE TRAVAIL MAXIMAL (DEFAULT OFF, FLEXIBLE, APPLIES TO ALL ROUNDS) */}
              <div style={{ background: '#1e293b', border: '1px solid #334155', padding: '8px 10px', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.78rem', color: '#f8fafc', fontWeight: '600' }}>
                    ⏱️ Temps de travail maximal
                  </span>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: '#0f172a',
                        border: '1px solid #334155',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        opacity: enforceMaxWorkTime ? 1 : 0.35,
                        pointerEvents: enforceMaxWorkTime ? 'auto' : 'none',
                        transition: 'opacity 0.2s ease'
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setMaxWorkHours(prev => Math.max(1, Math.round((prev - 0.5) * 2) / 2))}
                        disabled={!enforceMaxWorkTime || maxWorkHours <= 1}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          borderRight: '1px solid #334155',
                          color: (!enforceMaxWorkTime || maxWorkHours <= 1) ? '#475569' : '#38bdf8',
                          width: '24px',
                          height: '24px',
                          cursor: (!enforceMaxWorkTime || maxWorkHours <= 1) ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.9rem',
                          fontWeight: '700'
                        }}
                      >
                        −
                      </button>

                      <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px', minWidth: '40px', justifyContent: 'center', gap: '2px' }}>
                        <input
                          type="number"
                          min="1"
                          max="24"
                          step="0.5"
                          disabled={!enforceMaxWorkTime}
                          value={maxWorkHours}
                          onChange={e => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val)) setMaxWorkHours(val);
                          }}
                          className="no-spinner"
                          style={{
                            width: '26px',
                            background: 'transparent',
                            color: enforceMaxWorkTime ? '#38bdf8' : '#64748b',
                            fontWeight: '700',
                            border: 'none',
                            outline: 'none',
                            fontSize: '0.78rem',
                            textAlign: 'center'
                          }}
                        />
                        <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '600' }}>h</span>
                      </div>

                      <button
                        type="button"
                        onClick={() => setMaxWorkHours(prev => Math.min(24, Math.round((prev + 0.5) * 2) / 2))}
                        disabled={!enforceMaxWorkTime || maxWorkHours >= 24}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          borderLeft: '1px solid #334155',
                          color: (!enforceMaxWorkTime || maxWorkHours >= 24) ? '#475569' : '#38bdf8',
                          width: '24px',
                          height: '24px',
                          cursor: (!enforceMaxWorkTime || maxWorkHours >= 24) ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.9rem',
                          fontWeight: '700'
                        }}
                      >
                        +
                      </button>
                    </div>

                    <IonicToggle
                      checked={enforceMaxWorkTime}
                      onChange={setEnforceMaxWorkTime}
                    />
                  </div>
                </div>
              </div>

              {/* CROISER LES JOURS */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', border: '1px solid #334155', padding: '8px 10px', borderRadius: '6px', gap: '8px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.78rem', color: '#f8fafc', fontWeight: '600' }}>
                    📅 Croiser les jours
                  </span>
                  <span style={{ fontSize: '0.66rem', color: '#94a3b8' }}>
                    {allowCrossDays
                      ? 'Les livraisons peuvent être déplacées entre les jours sélectionnés'
                      : 'Chaque livraison reste dans sa journée initiale'}
                  </span>
                </div>
                <IonicToggle
                  checked={allowCrossDays}
                  onChange={setAllowCrossDays}
                />
              </div>

              {/* CROISER LES TOURNÉES */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', border: '1px solid #334155', padding: '8px 10px', borderRadius: '6px', gap: '8px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.78rem', color: '#f8fafc', fontWeight: '600' }}>
                    🔄 Croiser les tournées
                  </span>
                  <span style={{ fontSize: '0.66rem', color: '#94a3b8' }}>
                    {allowCrossRounds
                      ? 'Les livraisons de tournées différentes peuvent être regroupées'
                      : 'Chaque livraison reste dans une mission de sa tournée'}
                  </span>
                </div>
                <IonicToggle
                  checked={allowCrossRounds}
                  onChange={setAllowCrossRounds}
                />
              </div>

              {/* RECHARGEMENT AU DÉPÔT (MULTI-TOURS) */}
              <div style={{ display: 'flex', flexDirection: 'column', background: '#1e293b', border: allowHubRefills ? '1px solid #38bdf8' : '1px solid #334155', padding: '8px 10px', borderRadius: '6px', gap: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '0.78rem', color: '#f8fafc', fontWeight: '600' }}>
                      🏭 Rechargement au dépôt (multi-tours)
                    </span>
                    <span style={{ fontSize: '0.66rem', color: '#94a3b8' }}>
                      {allowHubRefills
                        ? 'Un camion plein ou vide peut revenir au dépôt, recharger et repartir pour la même tournée'
                        : 'Un seul tour par camion et par tournée (le camion peut enchaîner une autre tournée)'}
                    </span>
                  </div>
                  <IonicToggle
                    checked={allowHubRefills}
                    onChange={setAllowHubRefills}
                  />
                </div>
                {allowHubRefills && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', fontSize: '0.7rem', color: '#cbd5e1' }}>
                    <span>⏱ Temps de rechargement au dépôt</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <input
                        type="number"
                        min={0}
                        max={240}
                        step={5}
                        value={hubReloadMins}
                        onChange={(e) => setHubReloadMins(e.target.value)}
                        style={{ width: '56px', background: '#0f172a', border: '1px solid #334155', borderRadius: '4px', color: '#f8fafc', padding: '2px 6px', fontSize: '0.72rem', textAlign: 'right' }}
                      />
                      <span>min</span>
                    </div>
                  </div>
                )}
              </div>

              {/* CLIENT TIME WINDOWS */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', border: '1px solid #334155', padding: '8px 10px', borderRadius: '6px' }}>
                <span style={{ fontSize: '0.78rem', color: '#f8fafc', fontWeight: '600' }}>
                  🕒 Respecter les créneaux horaires clients
                </span>
                <IonicToggle
                  checked={enforceTimeWindows}
                  onChange={setEnforceTimeWindows}
                />
              </div>

              {/* WEIGHT CAPACITY */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', border: '1px solid #334155', padding: '8px 10px', borderRadius: '6px' }}>
                <span style={{ fontSize: '0.78rem', color: '#f8fafc', fontWeight: '600' }}>
                  ⚖️ Respecter la capacité en poids (kg)
                </span>
                <IonicToggle
                  checked={enforceWeight}
                  onChange={setEnforceWeight}
                />
              </div>

              {/* VOLUME CAPACITY */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', border: '1px solid #334155', padding: '8px 10px', borderRadius: '6px' }}>
                <span style={{ fontSize: '0.78rem', color: '#f8fafc', fontWeight: '600' }}>
                  📦 Respecter la capacité en volume (m³)
                </span>
                <IonicToggle
                  checked={enforceVolume}
                  onChange={setEnforceVolume}
                />
              </div>

              {/* DEPENDENCIES */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', border: '1px solid #334155', padding: '8px 10px', borderRadius: '6px' }}>
                <span style={{ fontSize: '0.78rem', color: '#f8fafc', fontWeight: '600' }}>
                  🔗 Respecter les dépendances Pickup/Delivery
                </span>
                <IonicToggle
                  checked={enforceDependencies}
                  onChange={setEnforceDependencies}
                />
              </div>

              {/* FORCED PAUSES */}
              <div style={{ background: '#1e293b', border: '1px solid #334155', padding: '8px 10px', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.78rem', color: '#f8fafc', fontWeight: '600' }}>
                    ☕ Pauses obligatoires
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {enforceForcedPauses && (
                      <button
                        type="button"
                        onClick={() => setForcedPauses(prev => [...prev, { id: Date.now(), duration_mins: 30, start_time: '12:00', end_time: '14:00' }])}
                        style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', fontSize: '0.68rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '2px' }}
                      >
                        <Plus size={11} /> Ajouter
                      </button>
                    )}
                    <IonicToggle
                      checked={enforceForcedPauses}
                      onChange={setEnforceForcedPauses}
                    />
                  </div>
                </div>

                {enforceForcedPauses && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingTop: '4px', borderTop: '1px dashed #334155' }}>
                    {forcedPauses.map((fp, idx) => (
                      <div key={fp.id || idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#0f172a', padding: '4px 6px', borderRadius: '4px', border: '1px solid #334155', fontSize: '0.72rem' }}>
                        <select
                          value={fp.duration_mins}
                          onChange={e => {
                            const val = parseInt(e.target.value);
                            setForcedPauses(prev => prev.map((item, i) => i === idx ? { ...item, duration_mins: val } : item));
                          }}
                          style={{ background: '#1e293b', color: '#38bdf8', fontWeight: '700', border: '1px solid #334155', borderRadius: '4px', padding: '2px 4px', fontSize: '0.7rem' }}
                        >
                          <option value="15">15 min</option>
                          <option value="20">20 min</option>
                          <option value="30">30 min</option>
                          <option value="45">45 min</option>
                          <option value="60">1h00</option>
                        </select>
                        <span style={{ color: '#94a3b8' }}>entre</span>
                        <input
                          type="time"
                          value={fp.start_time}
                          onChange={e => {
                            const val = e.target.value;
                            setForcedPauses(prev => prev.map((item, i) => i === idx ? { ...item, start_time: val } : item));
                          }}
                          style={{ background: '#1e293b', color: '#38bdf8', fontWeight: '700', border: '1px solid #334155', borderRadius: '4px', padding: '2px 4px', fontSize: '0.7rem' }}
                        />
                        <span style={{ color: '#94a3b8' }}>à</span>
                        <input
                          type="time"
                          value={fp.end_time}
                          onChange={e => {
                            const val = e.target.value;
                            setForcedPauses(prev => prev.map((item, i) => i === idx ? { ...item, end_time: val } : item));
                          }}
                          style={{ background: '#1e293b', color: '#38bdf8', fontWeight: '700', border: '1px solid #334155', borderRadius: '4px', padding: '2px 4px', fontSize: '0.7rem' }}
                        />
                        <button
                          type="button"
                          onClick={() => setForcedPauses(prev => prev.filter((_, i) => i !== idx))}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px', marginLeft: 'auto', display: 'flex', alignItems: 'center' }}
                          title="Supprimer la pause"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div
          style={{
            background: '#1e293b',
            padding: '12px 20px',
            borderTop: '1px solid #334155',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}
        >
          {/* Status Alert if validation fails */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem' }}>
            {totalDeliveriesToOpti === 0 ? (
              <span style={{ color: '#fca5a5', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertTriangle size={15} color="#ef4444" /> Veuillez sélectionner au moins une livraison ou mission à optimiser.
              </span>
            ) : selectedTruckIds.length === 0 ? (
              <span style={{ color: '#fca5a5', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertTriangle size={15} color="#ef4444" /> Veuillez sélectionner au moins un véhicule.
              </span>
            ) : !allTrucksHaveDrivers ? (
              <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertTriangle size={15} color="#f59e0b" /> Chaque véhicule sélectionné doit avoir un chauffeur assigné.
              </span>
            ) : (
              <span style={{ color: '#94a3b8' }}>
                Prêt à optimiser <strong>{totalDeliveriesToOpti}</strong> livraisons réparties sur <strong>{selectedTruckIds.length}</strong> véhicule(s).
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              className="btn-action"
              style={{ padding: '8px 16px', fontSize: '0.82rem', background: 'transparent', color: '#94a3b8', borderColor: '#475569' }}
              onClick={onClose}
              disabled={isLoading}
            >
              Annuler
            </button>

            <button
              className="btn-action"
              disabled={totalDeliveriesToOpti === 0 || selectedTruckIds.length === 0 || !allTrucksHaveDrivers || isLoading}
              style={{
                padding: '8px 20px',
                fontSize: '0.82rem',
                background: '#38bdf8',
                color: '#0f172a',
                border: 'none',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: (totalDeliveriesToOpti === 0 || selectedTruckIds.length === 0 || !allTrucksHaveDrivers || isLoading) ? 0.6 : 1,
                cursor: (totalDeliveriesToOpti === 0 || selectedTruckIds.length === 0 || !allTrucksHaveDrivers || isLoading) ? 'not-allowed' : 'pointer'
              }}
              onClick={handleLaunch}
            >
              {isLoading ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  <span>Calcul de l'optimisation croisée...</span>
                </>
              ) : (
                <>
                  <Zap size={16} />
                  <span>Lancer l'optimisation croisée ({totalDeliveriesToOpti} livraisons)</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
